import { useMemo, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Ruler } from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════════════
// «Тело» — вес + замеры на ОДНОМ графике с тумблерами линий.
// 1 линия  → родные единицы (кг / % / см).
// 2+ линии → нормализация к старту=100% → видно корреляцию веса и объёмов.
// ═══════════════════════════════════════════════════════════════════════════

export interface MeasurementPoint {
  date: string;
  rawDate: string;
  bf: number | null;
  waist: number | null;
  neck: number | null;
  hips: number | null;
  glutes: number | null;
}
export interface WeightPoint {
  date: string;
  w: number;
  raw: string;
}

const SERIES = [
  { key: 'w', label: 'Вес', color: '#3DD8FF', unit: 'кг' },
  { key: 'bf', label: '% жира', color: '#10E58E', unit: '%' },
  { key: 'waist', label: 'Талия', color: '#FFB84D', unit: 'см' },
  { key: 'hips', label: 'Бёдра', color: '#A78BFA', unit: 'см' },
  { key: 'glutes', label: 'Ягодицы', color: '#F472B6', unit: 'см' },
  { key: 'neck', label: 'Шея', color: '#94A3B8', unit: 'см' },
] as const;

// Подсказка: реальные значения (см/%/кг) + изменение от старта в мульти-режиме
function BodyTooltip({ active, payload, label, multi }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#141A1F', border: '1px solid #222B33', borderRadius: 12, padding: '8px 10px' }}>
      <div style={{ color: '#9AA6B2', fontSize: 11, marginBottom: 4 }}>{label}</div>
      {payload.map((p: any) => {
        const s = SERIES.find((x) => x.key === p.dataKey);
        if (!s) return null;
        const raw = multi ? p.payload?.[`${p.dataKey}__raw`] : p.value;
        if (raw == null) return null;
        const pct = multi && typeof p.value === 'number' ? Math.round(p.value - 100) : null;
        return (
          <div key={p.dataKey} style={{ color: s.color, fontSize: 12, fontWeight: 600 }}>
            {s.label}: {Math.round(raw * 10) / 10} {s.unit}
            {pct !== null && (
              <span style={{ color: '#9AA6B2', fontWeight: 400 }}>
                {' '}({pct > 0 ? '+' : ''}{pct}% от старта)
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function BodyChart({ weight, measurements }: { weight: WeightPoint[]; measurements: MeasurementPoint[] }) {
  // Слияние по дню
  const rows = useMemo(() => {
    const byDay = new Map<string, any>();
    const put = (raw: string, label: string, key: string, val: number) => {
      const day = raw.slice(0, 10);
      const cur = byDay.get(day) ?? { raw, label };
      cur[key] = val;
      byDay.set(day, cur);
    };
    weight.forEach((w) => put(w.raw, w.date, 'w', w.w));
    measurements.forEach((m) => {
      if (m.bf !== null) put(m.rawDate, m.date, 'bf', m.bf);
      if (m.waist !== null) put(m.rawDate, m.date, 'waist', m.waist);
      if (m.neck !== null) put(m.rawDate, m.date, 'neck', m.neck);
      if (m.hips !== null) put(m.rawDate, m.date, 'hips', m.hips);
      if (m.glutes !== null) put(m.rawDate, m.date, 'glutes', m.glutes);
    });
    return Array.from(byDay.values()).sort((a, b) => a.raw.localeCompare(b.raw));
  }, [weight, measurements]);

  // Доступные серии — где есть хотя бы 1 точка (1 точка = кружок; 2+ = линия)
  const available = useMemo(
    () => SERIES.filter((s) => rows.some((r) => r[s.key] != null)),
    [rows],
  );

  const [active, setActive] = useState<string[]>([]);
  // По умолчанию: вес (если есть), иначе первая доступная
  const defaultKeys = available.some((s) => s.key === 'w')
    ? ['w']
    : available.slice(0, 1).map((s) => s.key);
  const activeKeys = (active.length ? active : defaultKeys).filter((k) =>
    available.some((s) => s.key === k),
  );
  const multi = activeKeys.length >= 2;

  const data = useMemo(() => {
    if (activeKeys.length === 0) return [];
    if (!multi) {
      const k = activeKeys[0];
      return rows.map((r) => ({ label: r.label, [k]: r[k] ?? null }));
    }
    // Нормализация к первому ненулевому значению каждой серии = 100
    const base: Record<string, number> = {};
    for (const k of activeKeys) {
      const first = rows.find((r) => r[k] != null);
      if (first) base[k] = first[k];
    }
    return rows.map((r) => {
      const o: any = { label: r.label };
      for (const k of activeKeys) {
        o[k] = r[k] != null && base[k] ? (r[k] / base[k]) * 100 : null;
        o[`${k}__raw`] = r[k] ?? null; // реальное значение — для подсказки
      }
      return o;
    });
  }, [rows, activeKeys, multi]);

  if (available.length === 0) return null;

  function toggle(key: string) {
    setActive((prev) => {
      const cur = prev.length ? prev : activeKeys;
      if (cur.includes(key)) {
        const next = cur.filter((k) => k !== key);
        return next.length ? next : cur; // держим минимум 1 линию
      }
      return [...cur, key];
    });
  }

  const singleSeries = !multi ? SERIES.find((s) => s.key === activeKeys[0]) : null;
  // Активные серии, где всего 1 точка → линию не построить (нужен 2-й замер в другой день)
  const onePointActive = activeKeys
    .filter((k) => rows.filter((r) => r[k] != null).length === 1)
    .map((k) => SERIES.find((s) => s.key === k)?.label)
    .filter(Boolean);

  return (
    <div className="card p-4 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <Ruler size={14} className="text-emerald-glow" />
        <div className="label">Тело</div>
        {multi && <span className="text-[10px] text-ink-dim ml-auto">от старта = 100%</span>}
      </div>

      {/* Тумблеры линий */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {available.map((s) => {
          const on = activeKeys.includes(s.key);
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => toggle(s.key)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all active:scale-95 flex items-center gap-1.5 ${
                on ? 'text-ink' : 'text-ink-dim border-line'
              }`}
              style={on ? { borderColor: s.color, background: `${s.color}1a` } : {}}
            >
              <span
                style={{ width: 7, height: 7, borderRadius: 7, background: on ? s.color : '#5A6573' }}
              />
              {s.label}
            </button>
          );
        })}
      </div>

      <div className="h-44">
        <ResponsiveContainer>
          <LineChart data={data}>
            <CartesianGrid stroke="#222B33" strokeDasharray="3 3" />
            <XAxis dataKey="label" stroke="#5A6573" fontSize={11} />
            <YAxis
              stroke="#5A6573"
              fontSize={11}
              domain={['auto', 'auto']}
              tickFormatter={(v) => (multi ? `${Math.round(v)}%` : `${v}${singleSeries?.unit ?? ''}`)}
            />
            <Tooltip content={<BodyTooltip multi={multi} />} />
            {activeKeys.map((k) => {
              const s = SERIES.find((x) => x.key === k)!;
              return (
                <Line
                  key={k}
                  type="monotone"
                  dataKey={k}
                  stroke={s.color}
                  strokeWidth={2}
                  dot={{ fill: s.color, r: 4 }}
                  activeDot={{ r: 6 }}
                  connectNulls
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {onePointActive.length > 0 && (
        <p className="text-[10px] text-warn mt-2">
          {onePointActive.join(', ')}: пока 1 замер — показан точкой. Линия появится со второго замера в другой день.
        </p>
      )}
      <p className="text-[10px] text-ink-dim mt-2">
        {multi
          ? 'Ось — в % от старта (так кг/см/% сравнимы). Реальные значения — при нажатии на график. Хочешь одну в см — оставь только её.'
          : 'Включи ещё линию (талию, % жира…), чтобы наложить и сравнить тренды.'}
      </p>
    </div>
  );
}
